import { useMemo, useRef } from 'react';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { useTranslation } from 'next-i18next/pages';

import { Flex, Stack, Text } from '@highlight-cp/core-ui';

import {
  AtMentionDropdown,
  InlineEditableInputError,
  InlineEditableInputMaxLength,
} from './components';
import { DEFAULT_DEBOUNCE_MS, DEFAULT_MAX_LENGTH } from './constants';
import { useAtMentions, useEventHandlers, useShowMaxLengthWarning, useUpdateValue } from './hooks';
import classes from './inline-editable-input.module.css';
import type { AtMentionOption } from './types';

export interface InlineEditableInputProps {
  ariaLabel?: string;
  atMentionOptions?: AtMentionOption[];
  className?: string;
  classNames?: {
    root?: string;
    input?: string;
    error?: string;
  };
  commitOnBlur?: boolean;
  debounceMs?: number;
  disabled?: boolean;
  enableLineBreaks?: boolean;
  error?: string | React.ReactNode;
  maxLength?: number;
  onChange?: (value: string) => void;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onMouseOver?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onMouseOut?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  onInput?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  value: string;
  required?: boolean;
  truncate?: boolean;
  underline?: boolean;
}

/**
 * ### InlineEditableInput
 *
 * @description
 * A contentEditable inline input component that supports markdown formatting,
 * debounced onChange, character limits, and placeholder text. The component
 * automatically converts between markdown and HTML for editing/display.
 *
 * @usage
 * When using this component, it's recommended you maintain your own local state
 * for the `value` instead of directly passing in a value from BE data to avoid
 * clobbering updates to what the user is typing.
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState(BEData.value);
 *
 * const handleChange = (value: string) => {
 *   setValue(value);
 *   // Store value in API call
 *   if (apiError) {
 *     // Reset value to the BE data value if error
 *     setValue(BEData.value);
 *   }
 * };
 *
 * <InlineEditableInput
 *   value={value}
 *   onChange={handleChange}
 *   placeholder="Enter your text here..."
 *   maxLength={500}
 *   enableLineBreaks={true}
 * />
 * ```
 * @param props - Component props
 * @returns React component
 */

const InlineEditableInput = ({
  ariaLabel,
  atMentionOptions,
  className,
  classNames,
  commitOnBlur = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  disabled = false,
  enableLineBreaks = false,
  error,
  maxLength = DEFAULT_MAX_LENGTH,
  onBlur,
  onClick,
  onChange,
  onFocus,
  /* NOTE: this is not debounced, you probably want to use onChange instead. onInput is for certain use cases where you want to capture every keystroke. */
  onInput,
  onMouseOver,
  onMouseOut,
  placeholder,
  required = false,
  truncate = false,
  underline = false,
  value: rawValue,
}: InlineEditableInputProps) => {
  const { t } = useTranslation();
  // IMPORTANT: DO NOT READ spanRef.current.textContent DIRECTLY AND NEVER SEND IT TO THE BACKEND.
  const spanRef = useRef<HTMLSpanElement>(null);

  // IMPORTANT: sanitize the incoming value to prevent XSS attacks
  const value = useMemo(
    () =>
      DOMPurify.sanitize(rawValue, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }),
    [rawValue],
  );

  // @-mention functionality (always call hook, but pass undefined options if not enabled)
  const atMentions = useAtMentions({
    options: atMentionOptions,
    spanRef,
  });

  // Handles all user interactions and rendering state updates
  const {
    isShowingPlaceholder,
    handleInput,
    handleKeyDown,
    handleFocus,
    handlePaste,
    handleBlur,
    isFocused,
    rawCharacterCount,
    shouldFlashMaxLength,
  } = useEventHandlers({
    commitOnBlur,
    debounceMs,
    enableLineBreaks,
    maxLength,
    onBlur,
    onChange,
    onInput,
    onFocus,
    placeholder,
    spanRef,
    value,
    atMentions,
  });

  // Updates setting the initial value and updating it based on user input
  useUpdateValue({ isFocused, spanRef, value, placeholder, atMentionOptions });

  // Shows a warning if the user is close to the max length
  const showMaxLengthWarning = useShowMaxLengthWarning({
    isFocused,
    maxLength,
    currentLength: rawCharacterCount,
  });

  return (
    <Stack
      className={clsx(
        classes.root,
        className,
        classNames?.root,
        error && classes.hasError,
        truncate && classes.truncate,
        underline && classes.underline,
      )}
    >
      <span>
        <span
          className={clsx(
            classes.input,
            isShowingPlaceholder && classes.placeholder,
            error && classes.error,
            error && classNames?.error,
            underline && classes.underline,
            classNames?.input,
            truncate && classes.truncate,
          )}
          ref={spanRef}
          contentEditable={!disabled}
          data-placeholder={placeholder}
          aria-placeholder={placeholder}
          role="textbox"
          aria-label={
            ariaLabel ??
            t('Editable text input (max {{maxLength}} characters)', {
              maxLength,
            })
          }
          aria-multiline="false"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          suppressContentEditableWarning
          onClick={disabled ? undefined : onClick}
          onMouseOver={disabled ? undefined : onMouseOver}
          onMouseOut={disabled ? undefined : onMouseOut}
          onInput={disabled ? undefined : handleInput}
          onKeyDown={disabled ? undefined : handleKeyDown}
          onFocus={disabled ? undefined : handleFocus}
          onPaste={disabled ? undefined : handlePaste}
          onBlur={disabled ? undefined : handleBlur}
          title={disabled ? undefined : t('Click to edit')}
        />
        {required && (
          <span className={classes.requiredAsterisk}>
            <Text
              size="sm"
              c="var(--mantine-color-error)"
              role="alert"
              aria-live="polite"
              aria-atomic
            >
              *
            </Text>
          </span>
        )}
      </span>
      {(error || showMaxLengthWarning) && (
        <Flex justify="space-between" mt={3}>
          {error && <InlineEditableInputError error={error} />}
          {showMaxLengthWarning && (
            <InlineEditableInputMaxLength
              maxLength={maxLength}
              currentLength={rawCharacterCount}
              shouldFlashMaxLength={shouldFlashMaxLength}
            />
          )}
        </Flex>
      )}
      {atMentions.isOpen && atMentionOptions && (
        <AtMentionDropdown
          options={atMentions.filteredOptions}
          selectedIndex={atMentions.selectedIndex}
          position={atMentions.cursorPosition}
          onSelect={atMentions.insertMention}
        />
      )}
    </Stack>
  );
};

export default InlineEditableInput;
